/**
 * Pure helpers for a campaign's Advanced analytics (GET /campaigns/:id/failure-analysis).
 * Rows are the API's FailureRow shape: one per address that did not get the mail.
 */

/** Narrow the failed-address table. 'all' (or a missing value) leaves a filter off. */
export function filterFailureRows(rows, { reason = 'all', recovery = 'all', search = '' } = {}) {
  const q = String(search || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (reason !== 'all' && r.reason !== reason) return false;
    if (recovery !== 'all' && r.recovery !== recovery) return false;
    if (!q) return true;
    return [r.company_name, r.vendor_code, r.recipient, r.email_on_file].some((v) => String(v || '').toLowerCase().includes(q));
  });
}

/** Vendors that can be reached again without anybody supplying a new address. */
export function recoverableCount(recovery) {
  if (!recovery) return 0;
  return Number(recovery.automatic || 0) + Number(recovery.retry || 0) + Number(recovery.confirm_typo || 0);
}

/** A resend reaches a vendor only when its current record has a mailable address. */
const RESENDABLE = new Set(['automatic', 'retry']);

/**
 * Who a resend for these rows can go to, and — for the rest — why not, largest group first.
 * The server re-checks every vendor (reached, unsubscribed, still being retried) on the dry run.
 */
export function resendSplit(rows) {
  const ids = new Set();
  const blocked = new Map();
  for (const r of rows || []) {
    // A mail the provider is still retrying may yet arrive; resending it would be a duplicate.
    const why = r.retrying ? 'retrying' : RESENDABLE.has(r.recovery) ? null : r.recovery;
    if (!why) {
      // A shared mail (one copy per address) failed for every vendor it covered.
      for (const id of [r.vendor_id, ...(r.also_vendor_ids || [])]) if (id != null) ids.add(id);
    } else blocked.set(why, (blocked.get(why) || 0) + 1);
  }
  return {
    vendorIds: [...ids],
    blocked: [...blocked].map(([recovery, count]) => ({ recovery, count })).sort((a, b) => b.count - a.count),
  };
}

/**
 * Donut slices for "what happened to every address": delivered (a Mailtrap block counts as sent,
 * and sent is shown inside delivered — operator decision, 23 Oct 2026), each failure group, in
 * flight.
 */
export function outcomeSlices(a) {
  if (!a) return [];
  const groups = new Map();
  for (const r of a.reasons || []) groups.set(r.result, (groups.get(r.result) || 0) + r.count);
  return [
    { name: 'Delivered', value: Number(a.totals?.delivered || 0) + Number(a.totals?.sent || 0) },
    ...[...groups].map(([name, value]) => ({ name, value })).sort((x, y) => y.value - x.value),
    { name: 'Still in flight', value: Number(a.totals?.awaiting || 0) },
  ].filter((s) => s.value > 0);
}

/** The `n` biggest reasons, and the rest (biggest first) for a "show more". */
export function topReasons(reasons, n = 4) {
  const sorted = [...(reasons || [])].sort((x, y) => y.count - x.count);
  return { shown: sorted.slice(0, n), hidden: sorted.slice(n) };
}

/**
 * The vendors "Fix the email fields" looks at for one campaign: every failed vendor (and the
 * vendors a failed shared mail covered), plus the ones whose mail counts as sent without a
 * delivery — a Mailtrap block, often on an address with a typo a person can fix.
 */
export function fixScope(a) {
  const ids = new Set();
  for (const r of a?.rows || []) for (const id of [r.vendor_id, ...(r.also_vendor_ids || [])]) if (id != null) ids.add(id);
  for (const id of a?.unconfirmed_vendor_ids || []) ids.add(id);
  return [...ids];
}

/** What to do with the addresses that did not get it, biggest group first, zeros dropped. */
const TODO = [
  ['Can retry now', (r) => Number(r.automatic || 0) + Number(r.retry || 0)],
  ['Fix to confirm first', (r) => Number(r.confirm_typo || 0)],
  ['Need a new address', (r) => Number(r.needs_new_address || 0)],
  ['Unsubscribed — never', (r) => Number(r.do_not_mail || 0)],
  ['Already got it (shared address)', (r) => Number(r.already_reached || 0)],
];
export function todoSlices(a) {
  const r = a?.recovery;
  if (!r) return [];
  return TODO.map(([name, value]) => ({ name, value: value(r) }))
    .filter((x) => x.value > 0)
    .sort((x, y) => y.value - x.value);
}
